import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "./ui/button";
import { currency } from "../lib/format";
import { Bot, CheckCircle2, Heart, Info } from "lucide-react";

/**
 * SnacksFundTracker — public running-balance widget for the Robot Snacks
 * server maintenance fund ($1,400 threshold). Travel + events phrasing lives
 * only on Stripe / bank-deposit descriptors — never in the UI.
 *
 * `compact=true` renders a slimmer variant suitable for the Home page band.
 */
export default function SnacksFundTracker({ compact = false, className = "" }) {
    const [t, setT] = useState(null);

    async function load() {
        try {
            const { data } = await api.get("/accounting/snacks-tracker");
            setT(data);
        } catch {
            /* soft-fail */
        }
    }
    useEffect(() => {
        load();
        const iv = setInterval(load, 60_000);
        return () => clearInterval(iv);
    }, []);

    if (!t) return null;
    const funded = t.funded;
    const percent = t.percent;

    return (
        <div
            className={`relative overflow-hidden rounded-2xl border-2 p-6 lg:p-7 ${
                funded
                    ? "bg-[#eefaf0] border-[var(--rl-success)]"
                    : "bg-[#fff5f3] border-[var(--rl-primary)]"
            } ${className}`}
            data-testid="snacks-fund-tracker"
        >
            <div className="grid lg:grid-cols-[auto_1fr_auto] gap-5 items-center">
                <div
                    className={`w-14 h-14 rounded-full flex items-center justify-center text-white shrink-0 ${
                        funded ? "bg-[var(--rl-success)]" : "bg-[var(--rl-primary)]"
                    }`}
                >
                    {funded ? <CheckCircle2 className="w-7 h-7" /> : <Bot className="w-7 h-7" />}
                </div>

                <div className="min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                        <span
                            className={`eyebrow ${
                                funded ? "text-[var(--rl-success)]" : "text-[var(--rl-primary)]"
                            }`}
                            data-testid="snacks-fund-status"
                        >
                            {funded ? "Funded" : "Below threshold"}
                        </span>
                        <span className="eyebrow text-[var(--rl-text-3)]">Robot Snacks maintenance fund</span>
                    </div>

                    <div className="mt-1 font-serif text-2xl lg:text-3xl leading-tight">
                        {funded ? (
                            <>Servers funded for now — thank you.</>
                        ) : (
                            <>
                                <span data-testid="snacks-fund-shortfall">{currency(t.shortfall)}</span> to go —
                                keep the servers running.
                            </>
                        )}
                    </div>

                    <div className="mt-3 flex items-baseline gap-2">
                        <span
                            className="font-serif text-2xl lg:text-3xl text-[var(--rl-primary)]"
                            data-testid="snacks-fund-balance"
                        >
                            {currency(t.balance)}
                        </span>
                        <span className="text-sm text-[var(--rl-text-3)]">of {currency(t.target)}</span>
                        <span className="text-xs text-[var(--rl-text-3)] ml-auto lg:ml-2" data-testid="snacks-fund-percent">
                            {percent}%
                        </span>
                    </div>

                    <div className="mt-2 h-2.5 rounded-full bg-white overflow-hidden border border-[var(--rl-border)]">
                        <div
                            className={`h-full transition-all ${funded ? "bg-[var(--rl-success)]" : "bg-[var(--rl-primary)]"}`}
                            style={{ width: `${percent}%` }}
                        />
                    </div>

                    {!compact && (
                        <p className="mt-3 text-xs text-[var(--rl-text-2)] leading-relaxed inline-flex items-start gap-1.5" data-testid="snacks-fund-note">
                            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-70" />
                            <span>{t.note}</span>
                        </p>
                    )}
                </div>

                {!funded && (
                    <div className="flex flex-col gap-2 min-w-[170px]">
                        <Link to="/donate" data-testid="snacks-fund-donate-btn">
                            <Button className="btn-primary rounded-full h-11 px-5 w-full">
                                <Heart className="w-4 h-4 mr-2" fill="currentColor" /> Donate
                            </Button>
                        </Link>
                        <Link to="/robot-snacks">
                            <Button variant="outline" className="rounded-full h-11 px-5 w-full border-[var(--rl-primary)] text-[var(--rl-primary)]">
                                <Bot className="w-4 h-4 mr-2" /> Feed the robot
                            </Button>
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
